import React, {

  useEffect,

  useMemo,

  useCallback,

  useState,

} from "react";

import { useNavigate } from "react-router-dom";

import "./SupplierDashboard.css";

import SupplierSidebar from "./SupplierSidebar";

import SupplierTopbar from "./SupplierTopbar";

import supplierPortalService from "../../services/supplierPortalService";

const monthNames = [

  "January",

  "February",

  "March",

  "April",

  "May",

  "June",

  "July",

  "August",

  "September",

  "October",

  "November",

  "December",

];

const monthName = (value) =>

  monthNames[Number(value) - 1] || "";

const statementNumber = (item) =>

  `ST-${item.statement_year}-${String(

    item.statement_month

  ).padStart(2, "0")}`;

const documentStatus = (item) => {

  if (!item?.file_url) return "Not Available";

  if (!item?.expiry_date) return "Available";

  const expiry = new Date(item.expiry_date);

  return expiry < new Date() ? "Expired" : "Available";

};

const dashboardDocumentNames = [

  "RC",

  "Permit",

  "Fitness",

  "Insurance",

];

const vehicleLabel = (vehicle) =>

  vehicle?.vehicle_label ||

  vehicle?.vehicle_registration_number ||

  vehicle?.registration_number ||

  `Vehicle ${vehicle?.id || ""}`;

const SupplierDashboard = () => {

  const navigate = useNavigate();

  const [supplier, setSupplier] = useState(null);

  const [statements, setStatements] = useState([]);

  const [vehicles, setVehicles] = useState([]);

  const [selectedVehicle, setSelectedVehicle] = useState("");

  const [selectedYear, setSelectedYear] = useState("");

  const [selectedMonth, setSelectedMonth] = useState("");

  const [documents, setDocuments] = useState([]);

  const [selectedVehicleInfo, setSelectedVehicleInfo] =

    useState(null);

  const [loading, setLoading] = useState(true);

  const [documentsLoading, setDocumentsLoading] =

    useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const loadDashboard = useCallback(async () => {

    try {

      setLoading(true);

      setErrorMessage("");

      if (!supplierPortalService.isLoggedIn()) {

        navigate("/supplier/login", { replace: true });

        return;

      }

      const [

        profileResponse,

        statementsResponse,

        vehiclesResponse,

      ] = await Promise.all([

        supplierPortalService.getMe(),

        supplierPortalService.getStatements(),

        supplierPortalService.getVehicles(),

      ]);

      const profile =

        profileResponse?.supplier || null;

      const statementList =

        statementsResponse?.statements || [];

      const vehicleList =

        vehiclesResponse?.vehicles || [];

      setSupplier(profile);

      setStatements(

        Array.isArray(statementList)

          ? statementList

          : []

      );

      setVehicles(

        Array.isArray(vehicleList)

          ? vehicleList

          : []

      );

      if (profile) {

        localStorage.setItem(

          "supplier_profile",

          JSON.stringify(profile)

        );

      }

      if (vehicleList.length) {

        setSelectedVehicle(

          String(vehicleList[0].id)

        );

      }

    } catch (error) {

      console.error(

        "Supplier dashboard error:",

        error

      );

      if (!supplierPortalService.isLoggedIn()) {

        navigate("/supplier/login", {

          replace: true,

        });

        return;

      }

      setErrorMessage(

        error?.message ||

          "Unable to load supplier dashboard."

      );

    } finally {

      setLoading(false);

    }

  }, [navigate]);

  useEffect(() => {

    loadDashboard();

  }, [loadDashboard]);

  useEffect(() => {

    if (!selectedVehicle) {

      setSelectedYear("");

      setSelectedMonth("");

      setDocuments([]);

      setSelectedVehicleInfo(null);

      return;

    }

    const loadYears = async () => {

      try {

        setErrorMessage("");

        setDocuments([]);

        setSelectedMonth("");

        const response =

          await supplierPortalService.getVehicleYears(

            selectedVehicle

          );

        const rawYears = response?.years || [];

        const list = rawYears

          .map((item) =>

            typeof item === "object"

              ? item?.year ?? item?.value ?? item?.id

              : item

          )

          .filter(

            (item) =>

              item !== null &&

              item !== undefined &&

              String(item).trim() !== ""

          );

        const latestYear = list.length

          ? String(list[0])

          : "";

        setSelectedYear(latestYear);

        const info = vehicles.find(

          (item) =>

            String(item.id) ===

            String(selectedVehicle)

        );

        setSelectedVehicleInfo(info || null);

        if (!latestYear && info) {

          const fallbackYear =

            info.latest_year ||

            info.document_year ||

            info.year ||

            info.latestYear;

          if (fallbackYear) {

            setSelectedYear(String(fallbackYear));

          }

        }

      } catch (error) {

        setErrorMessage(

          error?.message ||

            "Unable to load vehicle years."

        );

      }

    };

    loadYears();

  }, [selectedVehicle, vehicles]);

  useEffect(() => {

    if (!selectedVehicle || !selectedYear) {

      setSelectedMonth("");

      return;

    }

    const loadMonths = async () => {

      try {

        setDocuments([]);

        setSelectedMonth("");

        const response =

          await supplierPortalService.getVehicleMonths(

            selectedVehicle,

            selectedYear

          );

        const rawMonths = response?.months || [];

        const list = rawMonths

          .map((item) =>

            typeof item === "object"

              ? item?.month ??

                item?.month_number ??

                item?.value ??

                item?.id

              : item

          )

          .filter(

            (item) =>

              item !== null &&

              item !== undefined &&

              String(item).trim() !== ""

          );

        setSelectedMonth(

          list.length ? String(list[0]) : ""

        );

      } catch (error) {

        setErrorMessage(

          error?.message ||

            "Unable to load vehicle months."

        );

      }

    };

    loadMonths();

  }, [selectedVehicle, selectedYear]);

  useEffect(() => {

    if (

      !selectedVehicle ||

      !selectedYear ||

      !selectedMonth

    ) {

      setDocuments([]);

      return;

    }

    const loadDocuments = async () => {

      try {

        setDocumentsLoading(true);

        setErrorMessage("");

        const response =

          await supplierPortalService.getVehicleDocuments(

            selectedVehicle,

            selectedYear,

            selectedMonth

          );

        setDocuments(response?.documents || []);

        if (response?.vehicle) {

          setSelectedVehicleInfo(response.vehicle);

        }

      } catch (error) {

        setDocuments([]);

        setErrorMessage(

          error?.message ||

            "Unable to load vehicle documents."

        );

      } finally {

        setDocumentsLoading(false);

      }

    };

    loadDocuments();

  }, [

    selectedVehicle,

    selectedYear,

    selectedMonth,

  ]);

  const recentStatements = useMemo(() => {

    return [...statements]

      .sort((a, b) => {

        const aKey =

          Number(a.statement_year || 0) * 100 +

          Number(a.statement_month || 0);

        const bKey =

          Number(b.statement_year || 0) * 100 +

          Number(b.statement_month || 0);

        return bKey - aKey;

      })

      .slice(0, 4);

  }, [statements]);

  const availableVehicleDocuments = useMemo(

    () =>

      documents.filter(

        (item) => Boolean(item.file_url)

      ).length,

    [documents]

  );

  const totalOutstanding = useMemo(() => {
    if (!Array.isArray(vehicles) || vehicles.length === 0) {
      return 0;
    }

    // Sum each assigned vehicle's outstanding amount once.
    return vehicles.reduce((total, vehicle) => {
      const amount = Number(vehicle?.outstanding_amount ?? 0);
      return total + (Number.isFinite(amount) ? amount : 0);
    }, 0);
  }, [vehicles]);

  const documentVisualization = useMemo(() => {

    const counts = {

      Available: 0,

      Expired: 0,

      "Not Available": 0,

    };

    documents.forEach((item) => {

      counts[documentStatus(item)] += 1;

    });

    const total = Math.max(documents.length, 1);

    return [

      {

        label: "Available",

        count: counts.Available,

        percent: Math.round(

          (counts.Available / total) * 100

        ),

        tone: "available",

      },

      {

        label: "Expired",

        count: counts.Expired,

        percent: Math.round(

          (counts.Expired / total) * 100

        ),

        tone: "expired",

      },

      {

        label: "Unavailable",

        count: counts["Not Available"],

        percent: Math.round(

          (counts["Not Available"] / total) * 100

        ),

        tone: "unavailable",

      },

    ];

  }, [documents]);

  const statementVisualization = useMemo(() => {

    const byYear = {};

    statements.forEach((item) => {

      const year = String(

        item.statement_year || "Unknown"

      );

      byYear[year] = (byYear[year] || 0) + 1;

    });

    const rows = Object.entries(byYear)

      .sort(([a], [b]) => Number(a) - Number(b))

      .slice(-4);

    const max = Math.max(

      ...rows.map(([, count]) => count),

      1

    );

    return rows.map(([year, count]) => ({

      year,

      count,

      height: Math.max(

        16,

        Math.round((count / max) * 100)

      ),

    }));

  }, [statements]);

  const dashboardDocuments = useMemo(() => {

    return dashboardDocumentNames

      .map((name) =>

        documents.find(

          (document) =>

            String(document.label || "")

              .trim()

              .toLowerCase() ===

            name.toLowerCase()

        )

      )

      .filter(Boolean);

  }, [documents]);

  const openDocument = (item) => {

    if (!item?.file_url) return;

    let url = item.file_url;

    if (

      url.startsWith("/") &&

      !url.startsWith("//")

    ) {

      const apiBase =

        process.env.REACT_APP_API_URL ||

        process.env.REACT_APP_API_BASE_URL ||

        "http://127.0.0.1:8000/api";

      const origin = apiBase.replace(/\/$/, "");

      url = `${origin}${url}`;

    }

    window.open(

      url,

      "_blank",

      "noopener,noreferrer"

    );

  };

  const goTo = (path) => {

    setSidebarOpen(false);

    navigate(path);

  };

  if (loading) {

    return (

      <div className="supplier-dashboard">

        <SupplierSidebar

          open={sidebarOpen}

          onClose={() => setSidebarOpen(false)}

        />

        <div className="supplier-main">

          <SupplierTopbar

            supplier={supplier}

            sidebarOpen={sidebarOpen}

            onMenuClick={() =>

              setSidebarOpen((value) => !value)

            }

          />

          <main className="supplier-dashboard-content">

            <div className="supplier-dashboard-loading">

              <div className="supplier-loading-spinner" />

              <p>

                Loading supplier dashboard...

              </p>

            </div>

          </main>

        </div>

      </div>

    );

  }

  return (

    <div className="supplier-dashboard">

      <SupplierSidebar

        open={sidebarOpen}

        onClose={() => setSidebarOpen(false)}

      />

      <div className="supplier-main">

        <SupplierTopbar

          supplier={supplier}

          sidebarOpen={sidebarOpen}

          onMenuClick={() =>

            setSidebarOpen((value) => !value)

          }

        />

        <main className="supplier-dashboard-content">

          {errorMessage && (

            <div className="supplier-dashboard-error">

              {errorMessage}

            </div>

          )}

          <section className="supplier-summary-grid">

            <div className="supplier-summary-card blue">

              <div className="summary-icon">▣</div>

              <div className="summary-content">

                <span>Assigned Vehicles</span>

                <strong>

                  {vehicles.length}

                </strong>

                <small>

                  Total vehicles assigned

                </small>

              </div>

            </div>

            <div className="supplier-summary-card green">

              <div className="summary-icon">✓</div>

              <div className="summary-content">

                <span>Vehicle Documents</span>

                <strong>

                  {availableVehicleDocuments}

                </strong>

                <small>

                  Available documents

                </small>

              </div>

            </div>

            <div className="supplier-summary-card purple">

              <div className="summary-icon">▰</div>

              <div className="summary-content">

                <span>Total Statements</span>

                <strong>

                  {statements.length}

                </strong>

                <small>

                  Generated statements

                </small>

              </div>

            </div>

            <div className="supplier-summary-card orange">

              <div className="summary-icon">₹</div>

              <div className="summary-content">

                <span>Total Outstanding</span>

                <strong>

                  ₹

                  {totalOutstanding.toLocaleString(

                    "en-IN",

                    {

                      minimumFractionDigits: 2,

                      maximumFractionDigits: 2,

                    }

                  )}

                </strong>

                <small>

                  Outstanding amount

                </small>

              </div>

            </div>

          </section>

          <div className="dashboard-lower-grid">

            <section className="supplier-vehicle-card dashboard-documents-section">

              <div className="supplier-section-header vehicle-header">

                <div className="section-title-wrap">

                  <div className="section-icon">

                    ▤

                  </div>

                  <div>

                    <h2>Vehicle Documents</h2>

                    <p>

                      Your latest available vehicle

                      documents.

                    </p>

                  </div>

                </div>

                <button

                  type="button"

                  className="view-all-btn"

                  onClick={() =>

                    goTo(

                      "/supplier/vehicle-documents"

                    )

                  }

                >

                  View All Documents

                  <span>→</span>

                </button>

              </div>

              {selectedVehicleInfo && (

                <div className="dashboard-vehicle-meta">

                  <div>

                    <span>VEHICLE</span>

                    <strong>

                      {vehicleLabel(

                        selectedVehicleInfo

                      )}

                    </strong>

                  </div>

                  <div>

                    <span>MODEL</span>

                    <strong>

                      {selectedVehicleInfo.vehicle_model ||

                        selectedVehicleInfo.model ||

                        "—"}

                    </strong>

                  </div>

                  <div>

                    <span>PERIOD</span>

                    <strong>

                      {selectedMonth && selectedYear

                        ? `${monthName(

                            selectedMonth

                          )} ${selectedYear}`

                        : "Latest available"}

                    </strong>

                  </div>

                </div>

              )}

              {documentsLoading ? (

                <div className="supplier-documents-loading">

                  <div className="supplier-loading-spinner small" />

                  <span>

                    Loading documents...

                  </span>

                </div>

              ) : dashboardDocuments.length ? (

                <div className="supplier-documents-grid dashboard-documents-grid">

                  {dashboardDocuments.map(

                    (document) => {

                      const status =

                        documentStatus(document);

                      return (

                        <article

                          className="supplier-document-card"

                          key={

                            document.key ||

                            document.id ||

                            document.label

                          }

                        >

                          <div className="document-card-top">

                            <div className="supplier-document-icon">

                              {document.label ===

                              "Fitness"

                                ? "✓"

                                : document.label ===

                                  "Insurance"

                                ? "▣"

                                : document.label ===

                                  "Permit"

                                ? "▥"

                                : "▤"}

                            </div>

                            <div className="supplier-document-info">

                              <h3>

                                {document.label}

                              </h3>

                              <p>

                                {document.expiry_date

                                  ? `Valid until ${document.expiry_date}`

                                  : "Document available"}

                              </p>

                            </div>

                          </div>

                          <div className="document-card-bottom">

                            <span

                              className={`supplier-document-status ${status

                                .toLowerCase()

                                .replace(

                                  /\s+/g,

                                  "-"

                                )}`}

                            >

                              {status}

                            </span>

                            <button

                              type="button"

                              className="supplier-document-view-btn"

                              disabled={

                                !document.file_url

                              }

                              onClick={() =>

                                openDocument(

                                  document

                                )

                              }

                            >

                              {document.file_url

                                ? "View"

                                : "Unavailable"}

                            </button>

                          </div>

                        </article>

                      );

                    }

                  )}

                </div>

              ) : (

                <div className="supplier-document-empty dashboard-empty-documents">

                  <div className="supplier-empty-icon">

                    ▤

                  </div>

                  <div>

                    <h3>

                      No vehicle documents

                      available

                    </h3>

                    <p>

                      Available documents will

                      appear here automatically.

                    </p>

                  </div>

                </div>

              )}

            </section>

            <section className="supplier-recent-card dashboard-statements-section">

              <div className="supplier-section-header">

                <div className="section-title-wrap">

                  <div className="section-icon purple-icon">

                    ▥

                  </div>

                  <div>

                    <h2>Recent Statements</h2>

                    <p>

                      Your latest supplier

                      statements.

                    </p>

                  </div>

                </div>

                <button

                  type="button"

                  className="view-all-btn"

                  onClick={() =>

                    goTo("/supplier/statements")

                  }

                >

                  View All Statements

                  <span>→</span>

                </button>

              </div>

              {recentStatements.length === 0 ? (

                <div className="supplier-empty-state">

                  <div className="supplier-empty-icon">

                    ▥

                  </div>

                  <h3>

                    No statements available

                  </h3>

                  <p>

                    Your supplier statements will

                    appear here.

                  </p>

                </div>

              ) : (

                <div className="supplier-table-wrap">

                  <table className="supplier-table">

                    <thead>

                      <tr>

                        <th>Statement No</th>

                        <th>Month</th>

                        <th>Year</th>

                        <th>Document</th>

                        <th>Action</th>

                      </tr>

                    </thead>

                    <tbody>

                      {recentStatements.map(

                        (item) => (

                          <tr key={item.id}>

                            <td>

                              {item.statement_number ||

                                statementNumber(

                                  item

                                )}

                            </td>

                            <td>

                              {item.statement_month

                                ? monthName(

                                    item.statement_month

                                  )

                                : "—"}

                            </td>

                            <td>

                              {item.statement_year ||

                                "—"}

                            </td>

                            <td>

                              <span

                                className={`supplier-table-status ${

                                  item.file_url

                                    ? "available"

                                    : "unavailable"

                                }`}

                              >

                                {item.file_url

                                  ? "Available"

                                  : "Unavailable"}

                              </span>

                            </td>

                            <td>

                              <button

                                type="button"

                                className="supplier-table-view-btn"

                                disabled={

                                  !item.file_url

                                }

                                onClick={() =>

                                  openDocument(

                                    item

                                  )

                                }

                              >

                                {item.file_url

                                  ? "View"

                                  : "Unavailable"}

                              </button>

                            </td>

                          </tr>

                        )

                      )}

                    </tbody>

                  </table>

                </div>

              )}

            </section>

          </div>

          <section className="dashboard-visual-grid">

            <div className="dashboard-visual-panel">

              <div className="dashboard-visual-heading">

                <div>

                  <strong>

                    Document Status

                  </strong>

                  <span>

                    Current vehicle document

                    health

                  </span>

                </div>

                <b>

                  {availableVehicleDocuments}/

                  {documents.length || 0}

                </b>

              </div>

              <div className="dashboard-status-bars">

                {documentVisualization.map(

                  (item) => (

                    <div

                      className="dashboard-status-row"

                      key={item.label}

                    >

                      <div className="dashboard-status-label">

                        <span>

                          {item.label}

                        </span>

                        <b>{item.count}</b>

                      </div>

                      <div className="dashboard-status-track">

                        <span

                          className={item.tone}

                          style={{

                            width: `${item.percent}%`,

                          }}

                        />

                      </div>

                    </div>

                  )

                )}

              </div>

            </div>

            <div className="dashboard-visual-panel statement-visual-panel">

              <div className="dashboard-visual-heading">

                <div>

                  <strong>

                    Statement Activity

                  </strong>

                  <span>

                    Statements generated by year

                  </span>

                </div>

                <b>{statements.length}</b>

              </div>

              {statementVisualization.length ? (

                <div className="dashboard-statement-bars">

                  {statementVisualization.map(

                    (item) => (

                      <div

                        className="dashboard-statement-bar-item"

                        key={item.year}

                      >

                        <div className="dashboard-statement-bar-track">

                          <span

                            style={{

                              height: `${item.height}%`,

                            }}

                          />

                        </div>

                        <strong>

                          {item.count}

                        </strong>

                        <small>

                          {item.year}

                        </small>

                      </div>

                    )

                  )}

                </div>

              ) : (

                <div className="dashboard-visual-empty">

                  No statement activity yet

                </div>

              )}

            </div>

          </section>

        </main>

      </div>

    </div>

  );

};

export default SupplierDashboard;